import { Trapezoid } from '@ritabygglov/core';
import { createDimensionId } from '../../../helpers/dimensionIds';
import { makeResizeCursorUrl } from '../../../helpers/utility';
import { useMeasurement } from '../../../hooks/useMeasurement';
import { computeTrapezoidVertices } from '../../../services/Actions/Trapezoid';
import { DimensionLine } from './components/DimensionLine';

type TrapezoidDimensionsProps = {
  element: Trapezoid;
  scalingFactor: number;
  scale: number;
  draggingDimensionId: string | null;
};

export const TrapezoidDimensions = ({
  element,
  scalingFactor,
  scale,
  draggingDimensionId,
}: TrapezoidDimensionsProps) => {
  const { formatLength } = useMeasurement();
  const absTopWidth = Math.abs(element.topWidth);
  const absBottomWidth = Math.abs(element.bottomWidth);
  const absHeight = Math.abs(element.height);

  const computed = computeTrapezoidVertices(
    element.topWidth,
    element.bottomWidth,
    element.height
  );

  if (!computed) return null;

  const { vertices } = computed;
  const { x, y } = element;

  const bottomLeft = { x: x + vertices[0][0], y: y + vertices[0][1] };
  const bottomRight = { x: x + vertices[1][0], y: y + vertices[1][1] };
  const topRight = { x: x + vertices[2][0], y: y + vertices[2][1] };
  const topLeft = { x: x + vertices[3][0], y: y + vertices[3][1] };

  const fontSize = element.style?.fontSize
    ? parseInt(element.style.fontSize as string, 10)
    : 16;

  const topWidthId = createDimensionId(element.id, 'top-width');
  const bottomWidthId = createDimensionId(element.id, 'bottom-width');
  const heightDimensionId = createDimensionId(element.id, 'height');

  const isTopWidthDragging = draggingDimensionId === topWidthId;
  const isBottomWidthDragging = draggingDimensionId === bottomWidthId;
  const isHeightDragging = draggingDimensionId === heightDimensionId;

  const topWidthOffset = element.dimensionOffsetTopWidth ?? 15;
  const topWidthLineStart: [number, number] = [
    topLeft.x,
    topLeft.y - topWidthOffset,
  ];
  const topWidthLineEnd: [number, number] = [
    topRight.x,
    topRight.y - topWidthOffset,
  ];
  const topWidthTextX = topLeft.x + (topRight.x - topLeft.x) / 2;
  const topWidthTextY = topLeft.y - topWidthOffset - 15;

  const bottomWidthOffset = element.dimensionOffsetBottomWidth ?? 15;
  const bottomWidthLineStart: [number, number] = [
    bottomLeft.x,
    bottomLeft.y + bottomWidthOffset,
  ];
  const bottomWidthLineEnd: [number, number] = [
    bottomRight.x,
    bottomRight.y + bottomWidthOffset,
  ];
  const bottomWidthTextX = bottomLeft.x + (bottomRight.x - bottomLeft.x) / 2;
  const bottomWidthTextY = bottomLeft.y + bottomWidthOffset + 20;

  const heightOffset = element.dimensionOffsetHeight ?? 15;
  const leftX = Math.min(topLeft.x, bottomLeft.x);
  const heightLineStart: [number, number] = [leftX - heightOffset, topLeft.y];
  const heightLineEnd: [number, number] = [leftX - heightOffset, bottomLeft.y];
  const heightTransform = `translate(${leftX - heightOffset - 20},${
    topLeft.y + (bottomLeft.y - topLeft.y) / 2
  })rotate(90)`;

  return (
    <g>
      <DimensionLine
        scale={scale}
        id={topWidthId}
        lineStart={topWidthLineStart}
        lineEnd={topWidthLineEnd}
        cornerStart={[topLeft.x, topLeft.y]}
        cornerEnd={[topRight.x, topRight.y]}
        isDragging={isTopWidthDragging}
        style={{ cursor: makeResizeCursorUrl(element.rotate + 90) }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          x={topWidthTextX}
          y={topWidthTextY}
        >
          {formatLength(absTopWidth * scalingFactor)}
        </text>
      </DimensionLine>

      <DimensionLine
        scale={scale}
        id={bottomWidthId}
        lineStart={bottomWidthLineStart}
        lineEnd={bottomWidthLineEnd}
        cornerStart={[bottomLeft.x, bottomLeft.y]}
        cornerEnd={[bottomRight.x, bottomRight.y]}
        isDragging={isBottomWidthDragging}
        style={{ cursor: makeResizeCursorUrl(element.rotate + 90) }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          x={bottomWidthTextX}
          y={bottomWidthTextY}
        >
          {formatLength(absBottomWidth * scalingFactor)}
        </text>
      </DimensionLine>

      <DimensionLine
        scale={scale}
        id={heightDimensionId}
        lineStart={heightLineStart}
        lineEnd={heightLineEnd}
        cornerStart={[topLeft.x, topLeft.y]}
        cornerEnd={[bottomLeft.x, bottomLeft.y]}
        isDragging={isHeightDragging}
        style={{ cursor: makeResizeCursorUrl(element.rotate) }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          transform={heightTransform}
        >
          {formatLength(absHeight * scalingFactor)}
        </text>
      </DimensionLine>
    </g>
  );
};
